import useAxios from '../hooks/useAxios';
import {
  IDENTITY_URL,
  UPDATE_PHONE_VERIFICATION_DATE_OF_PROMPTING,
  UPDATE_USER_2FA_URL,
  UPDATE_USER_DATA_URL,
  USER_PLAN_URL,
  REGISTER_ASSESSMENT_RESULTS_URL,
  ASSESSMENT_RESULTS_URL,
  USER_COUNT_URL,
  USER_CREDITS_URL,
  USER_TRIAL_URL,
  USER_CONSENT_URL
} from './constants';

/**
 * Hook for interacting with the User API.
 *
 * @returns {Object} An object containing functions to interact with the API.
 */
export const useUserApi = () => {
  const api = useAxios();

  /**
   * Gets the user's payment plan information.
   *
   * @returns {Promise} A Promise that resolves with the user's plan information.
   */
  const getUserPlan = () => api.get( USER_PLAN_URL );

  /**
   * Gets the user's identity data.
   *
   * @returns {Promise} A Promise that resolves with the user's identity data.
   */
  const getUserData = () => api.get( IDENTITY_URL );

  /**
   * Updates the user's data.
   *
   * @param {string} firstName - The user's first name.
   * @param {string} lastName - The user's last name.
   * @param {string} phoneNumber - The user's phone number.
   * @param {boolean} emailSubscription - Whether the user is subscribed to email notifications.
   * @returns {Promise} A Promise that resolves when the request is complete.
   */
  const updateUserData = ( firstName, lastName, phoneNumber, emailSubscription ) =>
    api.post( UPDATE_USER_DATA_URL, {
      first_name: firstName,
      last_name: lastName,
      phone_number: phoneNumber,
      is_email_subscribed: emailSubscription,
    } );

  /**
   * Updates the usage of multi-factor authentication for the user.
   *
   * @param {boolean} new2FAEnabledValue - The new value for 2FA enablement.
   * @returns {Promise} A Promise that resolves when the request is complete.
   */
  const updateMultiFactorAuthenticationUsage = new2FAEnabledValue =>
    api.post( `${ UPDATE_USER_2FA_URL }?is_enabled=${ new2FAEnabledValue }` );

  /**
   * Updates the time when the user was prompted for phone verification.
   *
   * @returns {Promise} A Promise that resolves when the request is complete.
   */
  const updateTimeUserWasPromptedForPhoneVerification = () => api.post( UPDATE_PHONE_VERIFICATION_DATE_OF_PROMPTING );

  const registerAssessmentResults = ( user ) => api.post( REGISTER_ASSESSMENT_RESULTS_URL, user );

  const getAssessmentResultsURL = async ( userData ) => {
    const response = await api.post( ASSESSMENT_RESULTS_URL, userData );
    return response.data;
  };
  /**
  * Gets the total user count.
  *
  * @returns {Promise} A Promise that resolves with the total user count.
  */
  const getUserCount = () => api.get( USER_COUNT_URL );

  /**
 * Get user credit
 *
 * @returns {Promise} A Promise that resolves with the total user credits.
 */
  const getUserCredits = () => api.get( USER_CREDITS_URL )


  const postStartUserTrial = () => api.post(USER_TRIAL_URL)

  const postUserConsent = (data) => api.post(USER_CONSENT_URL, data);


  return {
    getUserPlan,
    getUserData,
    updateUserData,
    updateMultiFactorAuthenticationUsage,
    updateTimeUserWasPromptedForPhoneVerification,
    registerAssessmentResults,
    getAssessmentResultsURL,
    getUserCount,
    getUserCredits,
    postStartUserTrial,
    postUserConsent
  };
};
